import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sun,
  Moon,
  ArrowRight,
  Zap,
  ShieldCheck,
  Wallet,
  MessageCircle,
  Smartphone,
  Users,
  Lock,
  Gift,
  Check,
} from "lucide-react";
import Reveal from "../components/Reveal";
import CookieConsent from "../components/CookieConsent";
import SuggestionBox from "../components/SuggestionBox";

const faqs = [
  {
    q: "What services do you offer?",
    a: "Virtual numbers for SMS verification, social media logs, VPN & texting apps, and gift delivery, all from one wallet.",
  },
  {
    q: "How do I fund my wallet?",
    a: "Generate a dedicated virtual account for fast transfers, or fund manually by sending to our bank account and uploading your receipt.",
  },
  {
    q: "Are OTPs delivered instantly?",
    a: "Most codes arrive in seconds, depending on the network. If a number doesn't receive a code before it expires, it's refunded to your wallet automatically.",
  },
  {
    q: "Can I pay in naira?",
    a: "Yes. Your wallet is funded in NGN, and you can switch the display to USD any time.",
  },
];

const services = [
  {
    icon: Smartphone,
    title: "Virtual Numbers",
    desc: "Receive SMS & OTP codes for WhatsApp, Telegram, Google and more.",
    iconBg: "bg-purple-100 dark:bg-purple-900/40",
    iconText: "text-purple-600 dark:text-purple-300",
  },
  {
    icon: Users,
    title: "Social Media Logs",
    desc: "Ready-to-use accounts for all major platforms, delivered fast.",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
    iconText: "text-emerald-600 dark:text-emerald-300",
  },
  {
    icon: Lock,
    title: "VPN & Texting Apps",
    desc: "Premium VPNs and texting apps at fair, transparent prices.",
    iconBg: "bg-indigo-100 dark:bg-indigo-900/40",
    iconText: "text-indigo-600 dark:text-indigo-300",
  },
  {
    icon: Gift,
    title: "Send Gifts Worldwide",
    desc: "Send thoughtful gifts to anyone, anywhere, paid from your wallet.",
    iconBg: "bg-pink-100 dark:bg-pink-900/40",
    iconText: "text-pink-600 dark:text-pink-300",
  },
];

const steps = [
  { n: 1, t: "Create Account", d: "Sign up in seconds and land in your dashboard." },
  { n: 2, t: "Fund Wallet", d: "Transfer to your virtual account, or fund manually with your receipt." },
  { n: 3, t: "Use Services", d: "Buy numbers, grab logs and send gifts, all from one wallet." },
];

const trust = [
  { icon: Zap, t: "Fast delivery", d: "Codes in seconds" },
  { icon: ShieldCheck, t: "Secure wallet", d: "Protected payments" },
  { icon: Wallet, t: "Naira funding", d: "Pay in NGN" },
  { icon: MessageCircle, t: "Real support", d: "Chat on Telegram" },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`accordion bg-white dark:bg-gray-800 border border-black/5 dark:border-white/10 rounded-xl overflow-hidden transition-colors ${
        open ? "open" : ""
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-ink dark:text-gray-100"
      >
        {q}
        <svg
          className="chev w-4 h-4 text-brandviolet-500 dark:text-brandviolet-400 shrink-0 ml-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className="accordion-body px-5">
        <p className="text-muted2 dark:text-gray-400 text-sm pb-4">{a}</p>
      </div>
    </div>
  );
};

const Landing = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const enabled = saved ? saved === "dark" : prefersDark;
    setDarkMode(enabled);
    document.documentElement.classList.toggle("dark", enabled);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  return (
    <div className="w-full min-h-screen bg-paper dark:bg-gray-900 text-ink dark:text-gray-100 overflow-x-hidden transition-colors duration-300">
      {/* ================= NAVBAR ================= */}
      <nav className="sticky top-0 z-50 bg-paper/80 dark:bg-gray-900/90 backdrop-blur-lg border-b border-black/5 dark:border-white/10 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/assets/icons/logo3.png"
              alt="LARRYDIGITALS"
              className="h-6 sm:h-9 md:h-10 lg:h-12 w-auto object-contain dark:invert dark:hue-rotate-180"
            />
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm text-muted2 dark:text-gray-400">
            <a href="#features" className="hover:text-ink dark:hover:text-white transition-colors">Services</a>
            <a href="#dashboard" className="hover:text-ink dark:hover:text-white transition-colors">Dashboard</a>
            <a href="#how" className="hover:text-ink dark:hover:text-white transition-colors">How it works</a>
            <a href="#faq" className="hover:text-ink dark:hover:text-white transition-colors">FAQ</a>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4">
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="p-2 rounded-lg text-muted2 dark:text-gray-300 hover:bg-black/[0.04] dark:hover:bg-white/10 transition-all duration-200 active:scale-90 shrink-0"
            >
              {darkMode ? (
                <Sun className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-amber-400" />
              ) : (
                <Moon className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              )}
            </button>
            <Link
              to="/login"
              className="text-xs sm:text-sm text-muted2 dark:text-gray-400 hover:text-ink dark:hover:text-white transition-colors"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="bg-brandviolet-500 hover:bg-brandviolet-600 text-white px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* ================= HERO ================= */}
      <section className="relative px-4 md:px-8 pt-10 sm:pt-14 md:pt-20 pb-6 md:pb-10 max-w-7xl mx-auto">
        <div className="pointer-events-none absolute -top-10 right-0 w-72 h-72 md:w-[28rem] md:h-[28rem] rounded-full bg-brandviolet-500/20 dark:bg-brandviolet-500/25 blur-3xl" />

        <div className="relative grid md:grid-cols-2 gap-10 md:gap-10 items-center">
          {/* ---- Text ---- */}
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-wide text-brandviolet-500 dark:text-brandviolet-300 bg-lilac dark:bg-white/10 rounded-full px-3 py-1 mb-5">
              For agencies, marketers &amp; freelancers
            </span>
            <h1 className="font-display font-semibold text-[2rem] sm:text-[2.6rem] md:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
              One dashboard for{" "}
              <span className="bg-gradient-to-r from-brandviolet-500 to-fuchsia-500 bg-clip-text text-transparent">
                every
              </span>{" "}
              digital service.
            </h1>
            <p className="mt-5 text-muted2 dark:text-gray-400 text-base md:text-lg max-w-xl">
              Virtual numbers for SMS verification, social media logs, VPN &amp; texting apps and
              gifts, all paid from one naira wallet.
            </p>

            <div className="mt-7 flex flex-wrap gap-3 sm:gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-brandviolet-500 hover:bg-brandviolet-600 text-white px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl font-semibold shadow-lg shadow-brandviolet-500/25 transition-colors"
              >
                Create Free Account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                className="border border-ink/15 dark:border-white/20 text-ink dark:text-gray-100 px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl font-semibold hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors"
              >
                Login
              </Link>
            </div>

            <div className="mt-8 flex gap-6 sm:gap-10 text-sm flex-wrap">
              <div>
                <div className="font-display text-xl sm:text-2xl font-semibold">150+</div>
                <div className="text-muted2 dark:text-gray-400">services</div>
              </div>
              <div>
                <div className="font-display text-xl sm:text-2xl font-semibold">99.9%</div>
                <div className="text-muted2 dark:text-gray-400">uptime</div>
              </div>
              <div>
                <div className="font-display text-xl sm:text-2xl font-semibold">24/7</div>
                <div className="text-muted2 dark:text-gray-400">wallet access</div>
              </div>
            </div>
          </Reveal>

          {/* ---- Phone (NOT inside Reveal, so it is visible immediately) ---- */}
          <div className="relative flex justify-center">
            <div className="relative w-[220px] sm:w-[250px] md:w-[290px]">
              <div className="absolute -inset-6 rounded-full bg-gradient-to-tr from-brandviolet-500/30 to-fuchsia-400/20 blur-2xl" />

              <div className="relative rounded-t-[2.4rem] border-[7px] border-b-0 border-ink dark:border-gray-600 bg-white overflow-hidden shadow-[0_30px_80px_-20px_rgba(91,33,182,0.5)]">
                <div className="aspect-[1080/1500]">
                  <img
                    src="/assets/landing/dashboard-mobile.webp"
                    alt="LarryDigitals mobile dashboard"
                    width="560"
                    height="1085"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* fade the bottom into the page */}
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-paper dark:from-gray-900 to-transparent pointer-events-none" />

              {/* floating chips */}
              <div className="absolute top-10 -right-8 sm:-right-12 animate-floatY bg-white dark:bg-gray-800 border border-black/5 dark:border-white/10 rounded-xl shadow-lg px-3 py-2.5">
                <div className="flex items-center gap-2 text-[11px] text-muted2 dark:text-gray-400 mb-0.5">
                  <span className="w-4 h-4 rounded-full bg-teal/15 text-teal flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </span>
                  Telegram OTP
                </div>
                <div className="font-semibold text-sm tracking-widest">482 913</div>
              </div>

              <div className="absolute bottom-24 -left-8 sm:-left-12 animate-floatY-slow bg-white dark:bg-gray-800 border border-black/5 dark:border-white/10 rounded-xl shadow-lg px-3 py-2.5 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-brandviolet-500/10 text-brandviolet-500 dark:text-brandviolet-300 flex items-center justify-center">
                  <Wallet className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-[10px] text-muted2 dark:text-gray-400 leading-tight">Wallet funded</div>
                  <div className="font-semibold text-sm leading-tight">+₦5,000</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {trust.map(({ icon: Icon, t, d }) => (
            <div
              key={t}
              className="flex items-center gap-3 rounded-xl bg-white dark:bg-gray-800 border border-black/5 dark:border-white/10 px-4 py-3 transition-colors"
            >
              <span className="w-9 h-9 rounded-lg bg-lilac dark:bg-white/10 text-brandviolet-500 dark:text-brandviolet-300 flex items-center justify-center shrink-0">
                <Icon className="w-[18px] h-[18px]" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">{t}</div>
                <div className="text-xs text-muted2 dark:text-gray-400 leading-tight mt-0.5">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section id="features" className="px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-widest text-brandviolet-500 dark:text-brandviolet-300 uppercase mb-3">
              What's inside
            </p>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl">
              Everything a digital operator actually needs
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 md:mt-10">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 80}>
                  <div className="h-full bg-white dark:bg-gray-800 border border-black/5 dark:border-white/10 rounded-2xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(109,40,217,0.3)]">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${s.iconBg}`}>
                      <Icon className={`w-5 h-5 ${s.iconText}`} />
                    </div>
                    <h3 className="font-display font-semibold text-lg mb-1.5">{s.title}</h3>
                    <p className="text-muted2 dark:text-gray-400 text-sm">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= DESKTOP SHOWCASE ================= */}
      <section
        id="dashboard"
        className="px-4 md:px-8 py-14 md:py-20 bg-lilac/60 dark:bg-gray-800/40 transition-colors"
      >
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold tracking-widest text-brandviolet-500 dark:text-brandviolet-300 uppercase mb-3">
              Your dashboard
            </p>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl">
              Your whole operation, one clean screen
            </h2>
            <p className="mt-3 text-muted2 dark:text-gray-400 text-sm md:text-base">
              Fund your wallet, buy in a few taps and track every order, on desktop or on your phone.
            </p>
          </Reveal>

          {/* Browser frame, NOT inside Reveal, so it shows immediately */}
          <div className="relative mt-10">
            <div className="absolute -inset-4 md:-inset-8 rounded-[2rem] bg-gradient-to-r from-brandviolet-500/25 via-fuchsia-400/15 to-brandviolet-500/25 blur-3xl" />

            <div className="relative rounded-xl md:rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-gray-800 overflow-hidden shadow-[0_40px_90px_-30px_rgba(91,33,182,0.45)]">
              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-700 border-b border-black/5 dark:border-white/10">
                <span className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                </span>
                <div className="mx-auto flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-3 py-1 text-[10px] sm:text-xs text-muted2 dark:text-gray-300">
                  <Lock className="w-3 h-3" />
                  larrydigitals.com/dashboard
                </div>
                <span className="w-[42px] shrink-0" />
              </div>
              <div className="aspect-[1366/679]">
                <img
                  src="/assets/landing/dashboard-desktop.webp"
                  alt="LarryDigitals desktop dashboard"
                  width="1366"
                  height="679"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-sm">
            {[
              "Live wallet balance with NGN / USD view",
              "Refer & earn built right in",
              "One-tap access to every service",
            ].map((p) => (
              <div key={p} className="flex items-center gap-2.5 text-muted2 dark:text-gray-300">
                <span className="w-5 h-5 rounded-full bg-teal/15 text-teal flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3" />
                </span>
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how" className="px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-center">
              Three steps, start to finish
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 sm:gap-10 mt-10 md:mt-14">
            {steps.map((step, i) => (
              <Reveal key={step.n} delay={i * 100} className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-brandviolet-500 to-brandviolet-600 text-white flex items-center justify-center font-display font-semibold mb-4 shadow-md shadow-brandviolet-500/25">
                  {step.n}
                </div>
                <h4 className="font-display font-semibold mb-2">{step.t}</h4>
                <p className="text-muted2 dark:text-gray-400 text-sm px-2 sm:px-4">{step.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="px-4 md:px-8 pb-14 md:pb-20 max-w-3xl mx-auto">
        <Reveal>
          <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-center mb-8 md:mb-10">
            Questions, answered
          </h2>
        </Reveal>
        <div className="space-y-3">
          {faqs.map((f) => (
            <FaqItem key={f.q} q={f.q} a={f.a} />
          ))}
        </div>
      </section>

      {/* ================= SUGGESTION BOX ================= */}
      <SuggestionBox />

      {/* ================= FINAL CTA ================= */}
      <section className="px-4 md:px-8 pb-14 md:pb-20">
        <Reveal>
          <div className="relative overflow-hidden max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-brandviolet-500 via-brandviolet-600 to-brandviolet-700 px-6 py-10 md:py-14 text-center">
            <div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-fuchsia-400/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
            <h2 className="relative font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-white">
              Ready to run everything from one wallet?
            </h2>
            <p className="relative mt-3 text-white/80 text-sm md:text-base max-w-lg mx-auto">
              Create your free account in seconds and start buying today.
            </p>
            <Link
              to="/signup"
              className="relative inline-flex items-center gap-2 mt-7 bg-white text-brandviolet-600 px-7 py-3.5 rounded-xl font-semibold shadow-lg hover:bg-lilac transition-colors"
            >
              Create Free Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="px-4 md:px-8 py-8 border-t border-black/5 dark:border-white/10 transition-colors">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src="/assets/icons/logo.png"
            alt="LARRYDIGITALS"
            className="h-7 sm:h-8 w-auto object-contain dark:invert dark:hue-rotate-180"
          />
          <div className="flex items-center gap-5 text-sm text-muted2 dark:text-gray-400">
            <Link to="/login" className="hover:text-ink dark:hover:text-white transition-colors">Login</Link>
            <Link to="/signup" className="hover:text-ink dark:hover:text-white transition-colors">Sign up</Link>
            <Link to="/cookie-policy" className="hover:text-ink dark:hover:text-white transition-colors">Cookie Policy</Link>
          </div>
          <p className="text-muted2 dark:text-gray-400 text-sm">
            © {new Date().getFullYear()} LARRYDIGITALS. All rights reserved.
          </p>
        </div>
      </footer>

      <CookieConsent policyHref="/cookie-policy" />
    </div>
  );
};

export default Landing;